<script lang="ts">
  import { onDestroy } from "svelte";
  import EmoticonIcon from "../EmoticonIcon.svelte";
  import Emoticons from "../Emoticons.svelte";
  import SendIcon from "../SendIcon.svelte";
  import FileUploader from "./FileUploader.svelte";
  import CloseIcon from "../../icons/close.svelte";
  import AttachmentIcon from "../AttachmentIcon.svelte";

  export let s;
  export let sendMessage: (message: string) => void;
  export let position;
  export let sendAttachment: (attachment: File) => void;
  export let showTypingIndicator: (showIndicator: boolean) => void;

  let message = "";
  let showEmoticons = false;
  let inputElement: HTMLTextAreaElement;
  let files: FileList | null = null;
  // Using this to only set the typing indicator once
  let typingSet = false;

  function setShowEmoticons() {
    showEmoticons = !showEmoticons;
  }

  function onKeyDown(e: KeyboardEvent) {
    const controlKeys = ["Meta", "Control", "Alt"];
    if (controlKeys.includes(e.key)) return;

    if (e.key === "Enter" || e.keyCode === 13) {
      e.preventDefault();
      relayMessage();
      typingSet = false;
    } else {
      if (!typingSet && e.key !== "Backspace") {
        showTypingIndicator(true);
        typingSet = true;
      }
    }

    if (message.length > 25 && inputElement.rows !== 2) {
      inputElement.rows = 2;
    }
  }

  function checkForEmptyMessage(message: string) {
    // keyDown runs before the message gets updated so it's
    // difficult to know when a message was typed then deleted.
    // This function checks for that condition and removes
    // the customer typing indicator shown to the agent.
    const emptyMessage = message.length === 0;
    if (emptyMessage && typingSet) {
      showTypingIndicator(false);
      typingSet = false;
    }
  }

  function relayMessage() {
    if (files && files[0]) {
      sendAttachment(files[0]);
      files = null;
    } else {
      sendMessage(message);
      message = "";
    }
  }

  function appendToMessage(emoticon: { native: string }) {
    message += emoticon.native;
    setShowEmoticons();
    inputElement.setSelectionRange(message.length, message.length);
    inputElement.focus();
  }

  $: checkForEmptyMessage(message);

  onDestroy(() => {
    showTypingIndicator(false);
  });
</script>

<div class={s.inputWrapper}>
  {#if files && files[0]}
    <div class={s.attachmentObject}>
      <div>
        <AttachmentIcon />
        {files[0].name}
      </div>
      <button
        type="button"
        on:click={() => (files = null)}
        style="cursor: pointer; pointer-events: all; background: none; border: none; padding: 0;"
        aria-label="Remove file"
      >
        <CloseIcon />
      </button>
    </div>
  {:else}
    <FileUploader {s} bind:files />
    <div 
      class={s.inputToolsWrapper} 
      on:click={setShowEmoticons}
      on:keydown={(e) => e.key === 'Enter' && setShowEmoticons()}
      tabindex="0"
      role="button"
    >
      <EmoticonIcon />
    </div>

    {#if showEmoticons}
      <Emoticons onClick={appendToMessage} {position} />
    {/if}

    <!-- svelte-ignore a11y-autofocus -->
    <textarea
      bind:this={inputElement}
      class={s.input}
      autofocus
      bind:value={message}
      on:keydown={onKeyDown}
      rows="1"
    />
  {/if}

  <div 
    class={s.sendBtn} 
    on:click={relayMessage}
    on:keydown={(e) => e.key === 'Enter' && relayMessage()}
    tabindex="0"
    role="button"
  >
    <SendIcon color="#fff" />
  </div>
</div>
